import React, { useState, useEffect } from 'react';
import { FaBinoculars, FaMapMarkerAlt, FaCalendarAlt, FaStar, FaSearch, FaFilter, FaFeatherAlt, FaFire, FaInfoCircle, FaExternalLinkAlt, FaEye } from 'react-icons/fa';
import { getBirdSightings, getBirdingHotspots, geocodeLocation } from '../../api/client';

const BirdWatcher = () => {
  const [sightings, setSightings] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [viewMode, setViewMode] = useState('sightings'); // 'sightings' or 'hotspots'
  const [locationMode, setLocationMode] = useState('gps'); // 'gps' or 'search'
  const [filters, setFilters] = useState({
    radiusKm: 25,
    daysBack: 14,
    notableOnly: false
  });
  const [selectedSighting, setSelectedSighting] = useState(null);

  // City/State search
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchLocationName, setSearchLocationName] = useState('');

  // Get user's location
  const getUserLocation = () => {
    setGettingLocation(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setSearchLocationName('');
          setGettingLocation(false);
          fetchData(loc);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setGettingLocation(false);
          alert('Could not get your location. Please enable location services.');
        },
        { timeout: 10000 }
      );
    } else {
      setGettingLocation(false);
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Search by city/state
  const handleCitySearch = async () => {
    if (!searchCity.trim()) {
      setSearchError('Please enter a city name');
      return;
    }

    setSearchError('');
    setGettingLocation(true);

    try {
      const data = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const loc = {
          lat: result.lat,
          lng: result.lng
        };
        setUserLocation(loc);
        setSearchLocationName(result.name);
        fetchData(loc);
      } else {
        setSearchError('Location not found. Try a different city name.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError('Failed to search location. Please try again.');
    } finally {
      setGettingLocation(false);
    }
  };

  // Fetch bird sightings
  const fetchSightings = async (loc) => {
    if (!loc) return;
    setLoading(true);
    try {
      const data = await getBirdSightings(loc.lat, loc.lng, {
        radius_km: filters.radiusKm,
        days_back: filters.daysBack,
        notable_only: filters.notableOnly
      });
      setSightings(data.sightings || []);
    } catch (error) {
      console.error('Error fetching bird sightings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch hotspots
  const fetchHotspots = async (loc) => {
    if (!loc) return;
    setLoading(true);
    try {
      const data = await getBirdingHotspots(loc.lat, loc.lng, filters.radiusKm);
      setHotspots(data.hotspots || []);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data based on view mode
  const fetchData = (loc) => {
    if (viewMode === 'sightings') {
      fetchSightings(loc);
    } else {
      fetchHotspots(loc);
    }
  };

  // Refetch when filters change
  useEffect(() => {
    if (userLocation) {
      fetchData(userLocation);
    }
  }, [filters, viewMode]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Render rarity badge
  const renderRarityBadge = (isRare) => {
    if (!isRare) return null;
    return (
      <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full flex items-center gap-1">
        <FaFire className="text-xs" />
        RARE
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Bird silhouettes */}
          <div className="absolute top-4 right-10 text-white/10 text-6xl transform rotate-12">
            <FaFeatherAlt />
          </div>
          <div className="absolute bottom-10 left-20 text-white/10 text-8xl transform -rotate-12">
            <FaBinoculars />
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaBinoculars className="text-2xl text-yellow-300" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Bird Watching Alerts</h2>
              <p className="text-white/70">Real-time sightings near you</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">{sightings.length}</div>
              <div className="text-white/70 text-sm">Recent Sightings</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">
                {sightings.filter(s => s.is_rare).length}
              </div>
              <div className="text-white/70 text-sm">Rare Species</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl font-bold">{hotspots.length}</div>
              <div className="text-white/70 text-sm">Hotspots Found</div>
            </div>
          </div>
        </div>
      </div>

      {/* Location & Controls */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Location Mode Toggle */}
          <div className="flex bg-neutral-100 rounded-xl p-1">
            <button
              onClick={() => setLocationMode('gps')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                locationMode === 'gps'
                  ? 'bg-white shadow-sm text-emerald-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaMapMarkerAlt className="inline mr-2" />
              Use GPS
            </button>
            <button
              onClick={() => setLocationMode('search')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                locationMode === 'search'
                  ? 'bg-white shadow-sm text-emerald-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaSearch className="inline mr-2" />
              By City
            </button>
          </div>

          {/* Get Location Button (only in GPS mode) */}
          {locationMode === 'gps' && (
            <button
              onClick={getUserLocation}
              disabled={gettingLocation}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              {gettingLocation ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <FaMapMarkerAlt />
              )}
              {userLocation ? 'Update Location' : 'Use My Location'}
            </button>
          )}

          {/* View Mode Toggle */}
          <div className="flex bg-neutral-100 rounded-xl p-1 ml-auto">
            <button
              onClick={() => setViewMode('sightings')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'sightings'
                  ? 'bg-white shadow-sm text-emerald-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaEye className="inline mr-2" />
              Sightings
            </button>
            <button
              onClick={() => setViewMode('hotspots')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'hotspots'
                  ? 'bg-white shadow-sm text-emerald-600'
                  : 'text-neutral-600 hover:text-neutral-800'
              }`}
            >
              <FaMapMarkerAlt className="inline mr-2" />
              Hotspots
            </button>
          </div>
        </div>

        {/* City/State Search (only in search mode) */}
        {locationMode === 'search' && (
          <div className="mb-6 p-4 bg-emerald-50 rounded-xl">
            <h4 className="text-sm font-medium text-emerald-900 mb-3">Search by Location</h4>
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  placeholder="City (e.g., Cape Town)"
                  className="w-full px-4 py-2.5 bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchState}
                  onChange={(e) => setSearchState(e.target.value)}
                  placeholder="Region (e.g., Western Cape)"
                  className="w-full px-4 py-2.5 bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <input
                  type="text"
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  placeholder="Country (e.g., South Africa)"
                  className="w-full px-4 py-2.5 bg-white border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                />
              </div>
              <button
                onClick={handleCitySearch}
                disabled={gettingLocation}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {gettingLocation ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FaSearch />
                )}
                Search
              </button>
            </div>
            {searchError && (
              <p className="text-red-600 text-sm mt-2">{searchError}</p>
            )}
            {searchLocationName && (
              <p className="text-emerald-700 text-sm mt-2">
                <FaMapMarkerAlt className="inline mr-1" />
                Showing results near {searchLocationName}
              </p>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Radius */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Radius
            </label>
            <select
              value={filters.radiusKm}
              onChange={(e) => setFilters({ ...filters, radiusKm: parseInt(e.target.value) })}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            >
              <option value={10}>10 km (6 mi)</option>
              <option value={25}>25 km (15 mi)</option>
              <option value={50}>50 km (31 mi)</option>
            </select>
          </div>

          {/* Days Back */}
          {viewMode === 'sightings' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <FaCalendarAlt className="inline mr-2" />
                Time Period
              </label>
              <select
                value={filters.daysBack}
                onChange={(e) => setFilters({ ...filters, daysBack: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              >
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          )}

          {/* Notable Only */}
          {viewMode === 'sightings' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                <FaFire className="inline mr-2" />
                Filter
              </label>
              <label className="flex items-center gap-3 px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-100 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.notableOnly}
                  onChange={(e) => setFilters({ ...filters, notableOnly: e.target.checked })}
                  className="w-4 h-4 text-emerald-500 rounded focus:ring-emerald-500"
                />
                <span className="text-sm text-neutral-700">Rare species only</span>
              </label>
            </div>
          )}
        </div>

        {/* Location Status */}
        {userLocation && locationMode === 'gps' && (
          <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
            <FaMapMarkerAlt className="text-emerald-500" />
            <span>Searching near {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}</span>
          </div>
        )}
      </div>

      {/* Results */}
      {!userLocation ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-neutral-100">
          <FaMapMarkerAlt className="text-5xl text-neutral-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-700 mb-2">Location Required</h3>
          <p className="text-neutral-500 mb-6">We need your location to find bird sightings near you.</p>
          <button
            onClick={getUserLocation}
            disabled={gettingLocation}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
          >
            Enable Location
          </button>
        </div>
      ) : loading ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Scanning for feathered friends...</p>
        </div>
      ) : viewMode === 'sightings' ? (
        /* Sightings View */
        <div className="space-y-4">
          {sightings.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <FaBinoculars className="text-5xl text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Sightings Found</h3>
              <p className="text-neutral-500">Try expanding your search radius or time period.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sightings.map((sighting, idx) => (
                <div
                  key={idx}
                  className={`bg-white rounded-2xl shadow-sm hover:shadow-lg border transition-all cursor-pointer overflow-hidden ${
                    selectedSighting === idx ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-neutral-100'
                  }`}
                  onClick={() => setSelectedSighting(selectedSighting === idx ? null : idx)}
                >
                  {/* Bird Icon Header */}
                  <div className={`p-4 ${sighting.is_rare ? 'bg-gradient-to-r from-amber-50 to-orange-50' : 'bg-emerald-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          sighting.is_rare ? 'bg-gradient-to-br from-amber-400 to-orange-500' : 'bg-emerald-500'
                        } text-white`}>
                          <FaFeatherAlt className="text-xl" />
                        </div>
                        <div>
                          <h3 className="font-bold text-neutral-800">{sighting.species_name}</h3>
                          <p className="text-xs text-neutral-500 italic">{sighting.scientific_name}</p>
                        </div>
                      </div>
                      {renderRarityBadge(sighting.is_rare)}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-sm text-neutral-600 mb-2">
                      <FaMapMarkerAlt className="text-emerald-500" />
                      <span className="truncate">{sighting.location_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-600 mb-3">
                      <FaCalendarAlt className="text-emerald-500" />
                      <span>{formatDate(sighting.observation_date)}</span>
                      {sighting.count && (
                        <span className="ml-auto bg-neutral-100 px-2 py-0.5 rounded-full text-xs font-medium">
                          {sighting.count} observed
                        </span>
                      )}
                    </div>

                    {/* Expanded Info */}
                    {selectedSighting === idx && (
                      <div className="pt-3 border-t border-neutral-100 animate-fade-in">
                        <div className="flex flex-wrap gap-2 text-xs text-neutral-500 mb-3">
                          <span>Lat: {sighting.lat.toFixed(4)}</span>
                          <span>|</span>
                          <span>Lng: {sighting.lng.toFixed(4)}</span>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${sighting.lat},${sighting.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center text-xs py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaMapMarkerAlt className="inline mr-1" />
                            View on Map
                          </a>
                          <a
                            href={`https://www.allaboutbirds.org/guide/${sighting.species_name.replace(/ /g, '_')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center text-xs py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaExternalLinkAlt className="inline mr-1" />
                            Learn More
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Hotspots View */
        <div className="space-y-4">
          {hotspots.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <FaMapMarkerAlt className="text-5xl text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Hotspots Found</h3>
              <p className="text-neutral-500">Try expanding your search radius.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hotspots.map((hotspot, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-neutral-100 transition-all p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white">
                        <FaMapMarkerAlt className="text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-neutral-800">{hotspot.name}</h3>
                        <p className="text-sm text-neutral-500">{hotspot.distance_km} km away</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-600">{hotspot.species_count}</div>
                      <div className="text-xs text-neutral-500">species</div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${hotspot.lat},${hotspot.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors text-sm font-medium"
                    >
                      <FaMapMarkerAlt className="inline mr-2" />
                      Get Directions
                    </a>
                    <a
                      href={`https://ebird.org/hotspots?env.minX=${hotspot.lng - 0.01}&env.minY=${hotspot.lat - 0.01}&env.maxX=${hotspot.lng + 0.01}&env.maxY=${hotspot.lat + 0.01}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 transition-colors text-sm font-medium"
                    >
                      <FaExternalLinkAlt className="inline mr-2" />
                      eBird Details
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-emerald-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-emerald-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-emerald-900 mb-1">Data Source</h4>
          <p className="text-sm text-emerald-700">
            Bird sighting data is sourced from eBird, a project of the Cornell Lab of Ornithology.
            Observations are submitted by citizen scientists worldwide. Rare species are flagged
            based on eBird's review process. Visit{' '}
            <a
              href="https://ebird.org"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-emerald-900"
            >
              ebird.org
            </a>{' '}
            to contribute your own sightings!
          </p>
        </div>
      </div>
    </div>
  );
};

export default BirdWatcher;
